import * as React from "react"
import { Calendar, CalendarPlus } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

export interface SummaryTemplateField {
  label: string
  value: string
}

export interface CalendarEventData {
  title: string
  description?: string
  location?: string
  startTime: string // ISO 8601 format
  endTime: string // ISO 8601 format
}

export interface SummaryTemplateProps
  extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  fields: SummaryTemplateField[]
  calendarEvent?: CalendarEventData
  onAddToCalendar?: (provider: "google" | "apple" | "ics") => void
}

const SummaryTemplateInner = React.forwardRef<HTMLDivElement, SummaryTemplateProps>(
  ({ className, title, fields, calendarEvent, onAddToCalendar, ...props }, ref) => {
    // Generate calendar URLs
    const getGoogleCalendarUrl = React.useCallback(() => {
      if (!calendarEvent) return null
      const { title, description, location, startTime, endTime } = calendarEvent

      // Convert ISO to Google Calendar format (YYYYMMDDTHHMMSSZ)
      const formatGoogleDate = (isoDate: string) => {
        return new Date(isoDate).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
      }

      const params = new URLSearchParams({
        action: "TEMPLATE",
        text: title,
        dates: `${formatGoogleDate(startTime)}/${formatGoogleDate(endTime)}`,
        ...(description && { details: description }),
        ...(location && { location: location }),
      })

      return `https://calendar.google.com/calendar/render?${params.toString()}`
    }, [calendarEvent])

    const getAppleCalendarUrl = React.useCallback(() => {
      if (!calendarEvent) return null
      // Apple Calendar uses webcal:// or .ics files
      // We'll generate an ICS data URL
      return generateIcsDataUrl(calendarEvent)
    }, [calendarEvent])

    const generateIcsDataUrl = (event: CalendarEventData) => {
      const formatIcsDate = (isoDate: string) => {
        return new Date(isoDate).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
      }

      const icsContent = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//DEAI//Reservation//EN",
        "BEGIN:VEVENT",
        `DTSTART:${formatIcsDate(event.startTime)}`,
        `DTEND:${formatIcsDate(event.endTime)}`,
        `SUMMARY:${event.title}`,
        ...(event.description ? [`DESCRIPTION:${event.description.replace(/\n/g, "\\n")}`] : []),
        ...(event.location ? [`LOCATION:${event.location}`] : []),
        `UID:${Date.now()}@deai.app`,
        "END:VEVENT",
        "END:VCALENDAR"
      ].join("\r\n")

      return `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`
    }

    const handleAddToCalendar = React.useCallback((provider: "google" | "apple" | "ics") => {
      if (onAddToCalendar) {
        onAddToCalendar(provider)
        return
      }

      if (!calendarEvent) return

      let url: string | null = null

      switch (provider) {
        case "google":
          url = getGoogleCalendarUrl()
          if (url) window.open(url, "_blank", "noopener,noreferrer")
          break
        case "apple":
        case "ics":
          url = getAppleCalendarUrl()
          if (url) {
            // Create a temporary link to download the ICS file
            const link = document.createElement("a")
            link.href = url
            link.download = `reservation-${Date.now()}.ics`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
          break
      }
    }, [calendarEvent, onAddToCalendar, getGoogleCalendarUrl, getAppleCalendarUrl])

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-lg border border-border bg-card p-6 shadow-sm",
          className
        )}
        role="region"
        aria-label={title}
        {...props}
      >
        <div className="space-y-4">
          {/* Title */}
          <h3 className="font-bold text-xl text-foreground">
            {title}
          </h3>

          {/* Summary Fields */}
          <div className="space-y-3">
            {fields.map((field, index) => (
              <div
                key={index}
                className="flex justify-between items-baseline gap-4"
              >
                <span className="text-sm text-muted-foreground font-normal">
                  {field.label}:
                </span>
                <span className="text-base text-foreground font-medium text-right">
                  {field.value}
                </span>
              </div>
            ))}
          </div>

          {/* Calendar Buttons */}
          {calendarEvent && (
            <div className="pt-3 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                <CalendarPlus className="size-4" />
                Add to Calendar
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleAddToCalendar("google")}
                  aria-label="Add to Google Calendar"
                >
                  <Calendar className="size-3.5 mr-1.5" />
                  Google
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleAddToCalendar("apple")}
                  aria-label="Add to Apple Calendar"
                >
                  <Calendar className="size-3.5 mr-1.5" />
                  Apple/ICS
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
)

SummaryTemplateInner.displayName = "SummaryTemplateInner"

// Memoize to prevent re-renders when parent re-renders
const SummaryTemplate = React.memo(SummaryTemplateInner)
SummaryTemplate.displayName = "SummaryTemplate"

export { SummaryTemplate }
